<template>
  <div class="flex-1 pb-6 px-6 pt-3">
    <!-- Header Section -->
    <div class="bg-background border-b border-border px-8 py-2 -mx-6 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-bold text-foreground">Instagram Stream Widget</h1>
          <p class="text-muted-foreground text-sm mt-1">Manage your Instagram Stream widgets and installations</p>
        </div>
        <div class="flex items-center space-x-3">
          <button
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 font-semibold">
            Create Customer
          </button>
          <button
            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 py-2 bg-foreground hover:bg-foreground/90 text-background font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-6"
            @click="showCreateWidgetModal = true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="lucide lucide-plus mr-2 h-4 w-4">
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            Create Widget
          </button>
        </div>
      </div>
    </div>
    <hr class="mb-6">

    <div class="px-2 space-y-6">
      <!-- Filters and Search -->
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <span class="text-sm text-muted-foreground">Show</span>
          <b-form-select v-model="datatable.queries.per_page"
            class="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background w-20"
            :options="[10, 15, 25, 50, 100]" @change="getWidgets"></b-form-select>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-muted-foreground">Search:</span>
          <input v-model="datatable.queries.search"
            class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm w-64"
            placeholder="Search..." @keyup.enter="getWidgets" />
        </div>
      </div>

      <!-- Table -->
      <div class="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div class="p-0">
          <div v-if="!loadingWidgets" class="relative w-full overflow-auto">
            <table class="w-full caption-bottom text-sm">
              <thead class="[&_tr]:border-b">
                <tr class="border-b transition-colors hover:bg-muted/50">
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">#</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Widget</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Instagram Page</th>
                  <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Installation</th>
                  <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="[&_tr:last-child]:border-0">
                <tr v-for="(widget, index) in widgets" :key="widget.id"
                  class="border-b transition-colors hover:bg-muted/50">
                  <td class="p-4 align-middle font-medium">{{ (datatable.queries.page - 1) * datatable.queries.per_page
                    + index + 1 }}</td>
                  <td class="p-4 align-middle">{{ widget.widget_title || widgetNameFromSettings(widget) || 'N/A' }}</td>
                  <td class="p-4 align-middle">
                    <button
                      class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                      :disabled="gettingPage" @click="viewPage(widget)">
                      <b-spinner v-if="gettingPage" small />
                      <span v-else>View Page</span>
                    </button>
                  </td>
                  <td class="p-4 align-middle">
                    <button
                      class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                      @click="addWidget(widget.id)">
                      Add Widget
                    </button>
                  </td>
                  <td class="p-4 align-middle text-right">
                    <div class="flex justify-end space-x-2">
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                        :disabled="gettingWidget" @click="editWidget(widget)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                          class="lucide lucide-square-pen h-4 w-4 mr-1">
                          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path
                            d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z">
                          </path>
                        </svg>
                        <b-spinner v-if="gettingWidget" small />
                        <span v-else>Edit Widget</span>
                      </button>
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-9 rounded-md px-3"
                        :disabled="loadingRemove === widget.id" @click="remove(widget)">
                        <b-spinner v-if="loadingRemove === widget.id" small />
                        <template v-else>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide lucide-trash2 h-4 w-4">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                          <span>Delete</span>
                        </template>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8">
            <b-spinner></b-spinner>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between text-sm text-muted-foreground">
        <span>Total {{ totalWidgets }} Widgets</span>
        <div class="flex items-center space-x-2">
          <b-pagination v-model="datatable.queries.page" :total-rows="totalWidgets"
            :per-page="datatable.queries.per_page" class="modern-pagination pagination mb-0"
            @change="onPageChanged"></b-pagination>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <b-modal v-model="showDeleteModal" title="Delete Widget">
      <p v-if="selectedBusiness">
        Are you sure you want to delete widget "{{ selectedBusiness.widget_title }}"?
      </p>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false">Cancel</b-button>
        <b-button variant="danger" @click="handleRemove">Delete</b-button>
      </template>
    </b-modal>

    <!-- Edit Widget Modal -->
    <b-modal v-model="showEditWidgetModal" :hide-footer="true" body-class="edit-widget-modal-body-wrapper"
      header-class="align-items-center" modal-class="edit_widget_modal widget-editor widget-modal" size="lg" title="Edit Instagram Widget">
      <template v-slot:modal-header>
        <h5 class="text-white">Edit Widget</h5>
        <div class="float-right">
          <button class="btn btn-default btn-sm mr-2" @click="showEditWidgetModal = false">
            Cancel
          </button>
          <button class="btn btn-success btn-sm mr-2" @click="saveEditor(false)">
            <b-spinner v-if="savingEditor" style="width: 1rem; height: 1rem;"></b-spinner>
            Save
          </button>
          <button class="btn btn-success btn-sm" @click="saveEditor(true)">
            <b-spinner v-if="savingEditor" style="width: 1rem; height: 1rem;"></b-spinner>
            Save & Exit
          </button>
        </div>
      </template>
      <div class="edit-widget-modal-body" style="display: flex; flex-direction: row; flex: 1; min-height: 0; width: 100%;">
        <div class="container-fluid" style="display: flex; flex: 1; min-width: 0; padding: 0;">
          <div class="row" style="display: flex; flex-direction: row; flex-wrap: nowrap; flex: 1; width: 100%; margin: 0;">
            <div id="Navbar" class="sidebar insta-widget-editor-sidebar" style="flex: 0 0 360px; width: 360px; max-width: 360px; display: flex;">
              <div class="left-nav-side float-left" style="display: flex; flex-direction: column; width: 56px; min-width: 56px;">
                <button type="button" :class="{ active: editor.editing === 'source' }" @click="editor.editing = 'source'">
                  <i class="uil uil-search d-block"></i>
                  Source
                </button>
                <button type="button" :class="{ active: editor.editing === 'layout' }" @click="editor.editing = 'layout'">
                  <i class="uil uil-window-grid d-block"></i>
                  Layout
                </button>
                <button type="button" :class="{ active: editor.editing === 'post_style' }" @click="editor.editing = 'post_style'">
                  <i class="uil uil-brush-alt d-block"></i>
                  Post Style
                </button>
                <button type="button" :class="{ active: editor.editing === 'style' }" @click="editor.editing = 'style'">
                  <i class="uil uil-image-edit d-block"></i>
                  Style
                </button>
              </div>
              <ul class="nav nav-sidebar">
                <li v-if="editor.editing === 'source'" class="source">
                  <h4>Source</h4>
                  <ul class="list-unstyled">
                    <li class="d-flex">
                      <input v-model="editor.source.username" type="text" class="source_input">
                      <button class="source_button" @click="getWidget(true)">
                        <i class="uil uil-search"></i>
                      </button>
                    </li>
                    <li class="widget_theme">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Search Type</span>
                        <v-select v-model="editor.source.type" :clearable="false" :options="[
                          { label: 'Username', value: 'username' },
                          { label: 'Hashtag', value: 'hashtag' },
                        ]" :reduce="option => option.value" />
                      </span>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'layout'" class="layout">
                  <h4>Layout</h4>
                  <ul class="list-unstyled">
                    <li class="layouts_wrapper">
                      <span v-for="layout in layouts" :key="layout.name" class="layout-item">
                        <input
                          :id="'layout-' + layout.name"
                          v-model="editor.layout.layoutStyle"
                          type="radio"
                          class="btn-check"
                          name="layout-style-radio"
                          autocomplete="off"
                          :value="layout.name"
                        />
                        <label :for="'layout-' + layout.name" class="btn layout-option-btn">
                          <img :alt="layout.label" :src="'/images/widget-layouts/' + layout.name + '-icon.png'" />
                          <span>{{ layout.label }}</span>
                        </label>
                      </span>
                    </li>
                    <li>
                      <span>Number of Columns</span>
                      <v-select v-model="editor.layout.columns" :clearable="false" taggable :options="[2, 3, 4]" />
                    </li>
                    <li>
                      <span>Number of Rows</span>
                      <v-select v-model="editor.layout.rows" :clearable="false" taggable :options="[1, 2, 3, 4]" />
                    </li>
                    <li>
                      <span>Gap Between Posts</span>
                      <v-select v-model="editor.layout.padding" :clearable="false" taggable :options="[0, 5, 10, 20, 30]" />
                    </li>
                    <li>
                      <span>Profile Details</span>
                      <ToggleButton v-model="editor.layout.show_profile_details" :height="20" :width="45"
                        color="#22c55e" :sync="true"></ToggleButton>
                    </li>
                    <li v-if="editor.layout.show_profile_details">
                      <ul class="list-unstyled profile_details">
                        <li>
                          <span>Profile Picture</span>
                          <ToggleButton v-model="editor.layout.profile_details.profile_image" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Username</span>
                          <ToggleButton v-model="editor.layout.profile_details.username" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Follow Button</span>
                          <ToggleButton v-model="editor.layout.profile_details.follow_button" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Followers</span>
                          <ToggleButton v-model="editor.layout.profile_details.followers" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Following</span>
                          <ToggleButton v-model="editor.layout.profile_details.following" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Posts</span>
                          <ToggleButton v-model="editor.layout.profile_details.post_count" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Name</span>
                          <ToggleButton v-model="editor.layout.profile_details.name" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'post_style'" class="layout">
                  <h4>Post Style</h4>
                  <ul class="list-unstyled">
                    <li class="post_style_wrapper">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Post Style</span>
                        <v-select v-model="editor.post_style.post_style" :clearable="false" :searchable="false"
                          append-to-body :options="[
                          { label: 'Expanded', value: 'expanded' },
                          { label: 'Image Card', value: 'image_card' },
                        ]" :reduce="option => option.value" />
                      </span>
                    </li>
                    <li v-if="editor.post_style.post_style === 'expanded'" class="post_details">
                      <ul class="list-unstyled profile_details">
                        <li>
                          <span>Profile Picture</span>
                          <ToggleButton v-model="editor.post_style.post_details.profile_image" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Username</span>
                          <ToggleButton v-model="editor.post_style.post_details.username" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Likes Count</span>
                          <ToggleButton v-model="editor.post_style.post_details.likes_count" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Comments Count</span>
                          <ToggleButton v-model="editor.post_style.post_details.comments_count" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                        <li>
                          <span>Caption</span>
                          <ToggleButton v-model="editor.post_style.post_details.caption" :height="20" :width="45"
                            color="#22c55e" :sync="true"></ToggleButton>
                        </li>
                      </ul>
                    </li>
                    <li class="post_action_wrapper">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Post Action</span>
                        <v-select v-model="editor.post_style.post_action" :clearable="false" :searchable="false"
                          append-to-body :options="[
                          { label: 'Open in Modal', value: 'modal' },
                          { label: 'Open on Instagram', value: 'instagram' },
                        ]" :reduce="option => option.value" />
                      </span>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'style'" class="layout">
                  <h4>Widget Style</h4>
                  <ul class="list-unstyled">
                    <li class="widget_theme">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Widget Theme</span>
                        <v-select v-model="editor.styles.theme" :clearable="false" :options="[
                          { label: 'Light', value: 'light' },
                          { label: 'Dark', value: 'dark' },
                        ]" :reduce="option => option.value" />
                      </span>
                    </li>
                    <li class="header_styles">
                      <span>Header Background</span>
                      <PopperColor v-model="editor.styles.header_bg" title="Header Background" />
                    </li>
                    <li class="header_styles">
                      <span>Header Text Color</span>
                      <PopperColor v-model="editor.styles.header_text" title="Header Text Color" />
                    </li>
                    <li class="header_styles">
                      <span>Follow Button Background</span>
                      <PopperColor v-model="editor.styles.follow_button_bg" title="Follow Button Background" />
                    </li>
                    <li class="header_styles">
                      <span>Follow Button Text Color</span>
                      <PopperColor v-model="editor.styles.follow_button_text" title="Follow Button Text Color" />
                    </li>
                    <li class="header_styles">
                      <span>Load More Button Background</span>
                      <PopperColor v-model="editor.styles.load_more_button_bg" title="Load More Button Background" />
                    </li>
                    <li class="header_styles">
                      <span>Load More Button Text Color</span>
                      <PopperColor v-model="editor.styles.load_more_button_text" title="Load More Button Text Color" />
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
            <div class="editor-content instagram-widget" :class="{ 'dark': editor.styles.theme === 'dark' }" style="flex: 1; min-width: 0; overflow: auto;">
              <div v-if="profileFetchState === ''" class="fetch_loader instagram-stream-idle-message">
                <p class="text-muted-foreground mb-0">Enter an Instagram username in the <strong>Source</strong> section
                  and
                  click the search button to preview.</p>
                <p class="text-muted-foreground small mt-2 mb-0">You don't need to connect to social media.</p>
              </div>
              <div v-else-if="profileFetchState === 'fetching' || profileFetchState === 'error'" class="fetch_loader">
                <b-spinner v-if="profileFetchState === 'fetching'"></b-spinner>
                <h3 v-if="profileFetchState === 'error'" class="error_fetching">{{ profileFetchMessage }}</h3>
              </div>
              <div v-else-if="profileFetchState === 'success'">
                <div v-if="postsType === 'profile'">
                  <div v-if="editor.layout.show_profile_details">
                    <div :style="{ backgroundColor: editor.styles.header_bg, color: editor.styles.header_text }"
                      class="profile_details_container">
                      <div class="profile_details_left">
                        <div
                          v-if="editor.layout.profile_details.profile_image && profile && profile.profile_picture_url"
                          class="profile_picture_container rounded rounded-circle">
                          <img
                            :src="(profile.profile_picture_url) ? profile.profile_picture_url : '/images/widget-layouts/profile.jpg'"
                            class="profile_picture">
                        </div>
                        <div class="profile_names_container">
                          <div class="username_container">
                            <h3 v-if="editor.layout.profile_details.name" class="name"
                              :style="{ color: editor.styles.header_text }">{{ profile.name }}</h3>
                          </div>
                          <div class="username_container">
                            <h3 v-if="editor.layout.profile_details.username" class="username"
                              :style="{ color: editor.styles.header_text }">{{ profile.username }}</h3>
                          </div>
                        </div>
                      </div>
                      <div class="profile_insights_container">
                        <p v-if="editor.layout.profile_details.post_count" class="insight_count_tag"
                          :style="{ color: editor.styles.header_text }">
                          <strong class="post_count" :style="{ color: editor.styles.header_text }">{{
                            formatNumber(profile.media_count) }}</strong>
                          posts
                        </p>
                        <p v-if="editor.layout.profile_details.followers" class="insight_count_tag"
                          :style="{ color: editor.styles.header_text }">
                          <strong class="followers_count" :style="{ color: editor.styles.header_text }">{{
                            formatNumber(profile.followers_count) }}</strong>
                          followers
                        </p>
                        <p v-if="editor.layout.profile_details.following" class="insight_count_tag"
                          :style="{ color: editor.styles.header_text }">
                          <strong class="following_count" :style="{ color: editor.styles.header_text }">{{
                            formatNumber(profile.follows_count) }}</strong>
                          following
                        </p>
                      </div>
                      <div class="follow_button_container">
                        <button v-if="editor.layout.profile_details.follow_button" class="follow_button"
                          :style="{ backgroundColor: editor.styles.follow_button_bg, color: editor.styles.follow_button_text }"
                          @click="redirectToProfile">
                          <i class="uil uil-instagram"></i> Follow
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="editor.layout.layoutStyle === 'grid'" class="posts_container grid_layout_posts_container"
                  :style="{ gridTemplateColumns: `repeat(${editor.layout.columns}, 1fr)`, gap: editor.layout.padding + 'px' }">
                  <div v-for="post in imageCardPosts" :key="post.id" class="post image_card" @click="post_action(post)">
                    <div class="media_wrapper">
                      <i class="media_icon"
                        :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                      <img v-if="post.media_type === 'IMAGE' && post.media_product_type === 'FEED'"
                        :src="post.media_url" class="post_image" alt="Post image">
                      <img v-else-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image"
                        alt="Post image">
                      <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url" class="post_image"
                        alt="Post image">
                      <video v-if="post.media_type === 'VIDEO' && post.media_product_type === 'REELS'"
                        :src="post.media_url" class="post_image"></video>
                      <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                    </div>
                    <div class="post_insights_container">
                      <span class="d-flex gap-5">
                        <p class="post_like">
                          <i class="uil uil-heart-alt"></i>{{ formatNumber(post.like_count) }}
                        </p>
                        <p class="post_like ml-3">
                          <i class="uil uil-comment"></i>{{ formatNumber(post.comments_count) }}
                        </p>
                      </span>
                    </div>
                  </div>
                  <div v-for="post in expandedPosts" :key="post.id" class="post expanded">
                    <div class="post_header justify-content-between align-items-center">
                      <div class="d-flex justify-content-start align-items-center">
                        <div
                          v-if="editor.post_style.post_details.profile_image && profile && profile.profile_picture_url"
                          class="profile_image_container">
                          <img
                            :src="(profile.profile_picture_url) ? profile.profile_picture_url : '/images/widget-layouts/profile.jpg'"
                            :alt="profile.name" class="profile_image">
                        </div>
                        <div v-if="editor.post_style.post_details.username" class="post_username_container mx-1">
                          <h3 class="username">{{ profile.username }}</h3>
                        </div>
                      </div>
                      <div class="font-size-24 mx-2">
                        <a :href="'https://instagram.com/' + profile.username" target="_blank">
                          <i class="uil uil-instagram instagram_icon"></i>
                        </a>
                      </div>
                    </div>
                    <div class="post_image_container" @click="post_action(post)">
                      <div class="media_wrapper">
                        <i class="media_icon"
                          :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                        <img v-if="post.media_type === 'IMAGE' && post.media_product_type === 'FEED'"
                          :src="post.media_url" class="post_image" alt="Post image">
                        <img v-else-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image"
                          alt="Post image">
                        <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url" class="post_image"
                          alt="Post image">
                        <video v-if="post.media_type === 'VIDEO' && post.media_product_type === 'REELS'"
                          :src="post.media_url" class="post_image"></video>
                        <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                      </div>
                    </div>
                    <div class="post_footer" @click="post_action(post)">
                      <div class="post_insights_container">
                        <span class="d-flex gap-5">
                          <p v-if="editor.post_style.post_details.likes_count" class="post_like">
                            <i class="uil uil-heart-alt"></i>{{ formatNumber(post.like_count) }}
                          </p>
                          <p v-if="editor.post_style.post_details.comments_count" class="post_like ml-2">
                            <i class="uil uil-comment"></i>{{ formatNumber(post.comments_count) }}
                          </p>
                        </span>
                      </div>
                      <div v-if="editor.post_style.post_details.caption" class="post_caption_container">
                        <p class="caption">{{ post.caption }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="showLoadMore && editor.layout.layoutStyle === 'grid'" class="load_more_container">
                  <button class="load_more_button"
                    :style="{ backgroundColor: editor.styles.load_more_button_bg, color: editor.styles.load_more_button_text }"
                    @click="loadMore">Load More</button>
                </div>
                <div v-if="editor.layout.layoutStyle === 'slider'"
                  class="posts_container slider_layout_posts_container">
                  <Swiper :slides-per-view="editor.layout.columns" :space-between="editor.layout.padding"
                    :modules="modules" navigation :loop="true" class="mySwiper" :clickable="true" @swiper="onSwiper"
                    @slideChange="onSlideChange">
                    <SwiperSlide v-for="post in posts" :key="post.id">
                      <div v-if="editor.post_style.post_style === 'image_card' && post.media_url"
                        class="post image_card" @click="post_action(post)">
                        <div class="media_wrapper">
                          <i class="media_icon"
                            :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                          <img v-if="post.media_type === 'IMAGE' && post.media_product_type === 'FEED'"
                            :src="post.media_url" class="post_image" alt="Post image">
                          <img v-else-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image"
                            alt="Post image">
                          <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url" class="post_image"
                            alt="Post image">
                          <video v-if="post.media_type === 'VIDEO' && post.media_product_type === 'REELS'"
                            :src="post.media_url" class="post_image"></video>
                          <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url"
                            class="post_image"></video>
                        </div>
                        <div class="post_insights_container">
                          <p class="post_like">
                            <i class="uil uil-heart-alt"></i> {{ formatNumber(post.like_count) }}
                          </p>
                        </div>
                      </div>
                      <div v-if="editor.post_style.post_style === 'expanded' && post.media_url" class="post expanded"
                        @click="post_action(post)">
                        <div class="post_header">
                          <div class="profile_image_container">
                            <img
                              v-if="profile && profile.profile_picture_url && editor.post_style.post_details.profile_image"
                              :src="(profile.profile_picture_url) ? profile.profile_picture_url : '/images/widget-layouts/profile.jpg'"
                              :alt="profile.name" class="profile_image">
                          </div>
                          <div v-if="editor.post_style.post_details.username" class="post_username_container">
                            <h3 class="username">{{ profile.username }}</h3>
                          </div>
                        </div>
                        <div class="post_image_container">
                          <div class="media_wrapper">
                            <i class="media_icon"
                              :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                            <img v-if="post.media_type === 'IMAGE' && post.media_product_type === 'FEED'"
                              :src="post.media_url" class="post_image" alt="Post image">
                            <img v-else-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image"
                              alt="Post image">
                            <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url"
                              class="post_image" alt="Post image">
                            <video v-if="post.media_type === 'VIDEO' && post.media_product_type === 'REELS'"
                              :src="post.media_url" class="post_image"></video>
                            <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url"
                              class="post_image"></video>
                          </div>
                        </div>
                        <div class="post_footer">
                          <div class="post_insights_container">
                            <span class="d-flex gap-5">
                              <p v-if="editor.post_style.post_details.likes_count" class="post_like">
                                <i class="uil uil-heart-alt"></i>{{ formatNumber(formatNumber(post.like_count)) }}
                              </p>
                              <p v-if="editor.post_style.post_details.comments_count" class="post_like ml-2">
                                <i class="uil uil-comment"></i>{{ formatNumber(post.comments_count) }}
                              </p>
                            </span>
                          </div>
                          <div v-if="editor.post_style.post_details.caption" class="post_caption_container">
                            <p class="caption">{{ post.caption }}</p>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>

                <b-modal v-model="showPostModal" :hide-footer="true" title="Posts"
                  :header-class="{ 'dark': editor.styles.theme === 'dark' }" size="lG">
                  <div :class="{ 'dark': editor.styles.theme === 'dark' }" class="modal_posts_container">
                    <div class="post expanded">
                      <div class="post_header d-flex justify-content-between">
                        <div class="post_header_left d-flex align-items-center">
                          <div class="profile_image_container">
                            <img v-if="profile && profile.profile_picture_url"
                              :src="(profile.profile_picture_url) ? profile.profile_picture_url : '/images/widget-layouts/profile.jpg'"
                              :alt="profile.name" class="profile_image" @click="redirectToProfile">
                          </div>
                          <div class="post_username_container ml-2">
                            <h3 class="username" @click="redirectToProfile">{{ profile.username }}</h3>
                          </div>
                        </div>
                        <button class="follow_btn_post" @click="redirectToProfile">Follow</button>
                      </div>
                      <div class="post_image_container">
                        <div class="media_wrapper">
                          <i class="media_icon"
                            :class="{ 'uil uil-play': modalPost.media_type === 'VIDEO', 'uil uil-layers': modalPost.media_type === 'CAROUSEL_ALBUM' }"></i>
                          <img v-if="modalPost.media_type === 'IMAGE' && modalPost.media_product_type === 'FEED'"
                            :src="modalPost.media_url" class="post_image" alt="Post image">
                          <img v-else-if="modalPost.media_type === 'IMAGE'" :src="modalPost.media_url"
                            class="post_image" alt="Post image">
                          <img v-else-if="modalPost.media_type === 'CAROUSEL_ALBUM'" :src="modalPost.media_url"
                            class="post_image" alt="Post image">
                          <video v-if="modalPost.media_type === 'VIDEO' && modalPost.media_product_type === 'REELS'"
                            :src="modalPost.media_url" class="post_image" autoplay></video>
                          <video v-else-if="modalPost.media_type === 'VIDEO'" :src="modalPost.media_url"
                            class="post_image" autoplay></video>
                        </div>
                      </div>
                      <div class="post_footer">
                        <div class="post_insights_container">
                          <span class="d-flex gap-5">
                            <p class="post_like">
                              <strong>{{ formatNumber(modalPost.like_count) }} Likes</strong>
                            </p>
                          </span>
                        </div>
                        <div class="post_caption_container">
                          <p class="caption"><strong>{{ profile.username }}</strong> {{ modalPost.caption }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-modal>

              </div>

              <b-modal v-model="showPostModal" :hide-footer="true" title="Posts"
                :header-class="{ 'dark': editor.styles.theme === 'dark' }" size="lG">
                <div v-for="post in posts" :key="post.id" :class="{ 'dark': editor.styles.theme === 'dark' }"
                  class="modal_posts_container">
                  <div class="post expanded">
                    <div class="post_header d-flex justify-content-between">
                      <div class="post_header_left d-flex align-items-center">
                        <div class="profile_image_container">
                          <img v-if="profile && profile.profile_picture_url"
                            :src="(profile.profile_picture_url) ? profile.profile_picture_url : '/images/widget-layouts/profile.jpg'"
                            :alt="profile.name" class="profile_image" @click="redirectToProfile">
                        </div>
                        <div class="post_username_container ml-2">
                          <h3 class="username" @click="redirectToProfile">{{ profile.username }}</h3>
                        </div>
                      </div>
                      <button class="follow_btn_post" @click="redirectToProfile">Follow</button>
                    </div>
                    <div class="post_image_container" @click="post_action(post)">
                      <div class="media_wrapper">
                        <i class="media_icon"
                          :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                        <img v-if="post.media_type === 'IMAGE' && post.media_product_type === 'FEED'"
                          :src="post.media_url" class="post_image" alt="Post image">
                        <img v-else-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image"
                          alt="Post image">
                        <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url" class="post_image"
                          alt="Post image">
                        <video v-if="post.media_type === 'VIDEO' && post.media_product_type === 'REELS'"
                          :src="post.media_url" class="post_image"></video>
                        <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                      </div>
                      <div class="post_insights_container">
                        <span class="d-flex gap-5">
                          <p class="post_like">
                            <strong>{{ formatNumber(modalPost.like_count) }} Likes</strong>
                          </p>
                        </span>
                      </div>
                      <div class="post_caption_container">
                        <p class="caption"><strong>{{ profile.username }}</strong> {{ post.caption }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </b-modal>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Create Widget Modal -->
    <b-modal v-model="showCreateWidgetModal" title="Create Widget" :hide-footer="true">
      <span for="">Widget Title</span>
      <TextInput v-model="newWidgetTitle" class="d-block mt-2" />
      <button class="ml-1 mt-3 btn btn-dark-blue btn-sm" @click="createWidget">
        <i class="uil uil-plus"></i>
        Create Widget
      </button>
    </b-modal>

    <!-- Add Widget Modal -->
    <b-modal v-model="showAddWidgetModal" title="Add Widget" size="lg" :hide-footer="true">
      <textarea cols="30" rows="5" class="form-control" style="white-space: pre-line" v-html="embedHTML"></textarea>
      <b-button class="mt-5" @click="copyToClipboard">Copy Widget</b-button>
    </b-modal>
  </div>
</template>

<script>
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/swiper-vue.mjs'
import 'swiper/swiper-bundle.css'
import { ToggleButton } from 'vue-js-toggle-button'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import PopperColor from '@components/Common/PopperColor'
import Vue from 'vue'

export default {
  name: 'InstaWidget',
  components: {
    Swiper,
    SwiperSlide,
    ToggleButton,
    'v-select': vSelect,
    PopperColor,
  },
  setup() {
    const onSwiper = (swiper) => {
    }
    const onSlideChange = () => {
    }
    return {
      onSwiper,
      onSlideChange,
      modules: [Navigation, Pagination, Scrollbar, A11y],
    }
  },
  data() {
    return {
      layouts: [
        {
          label: 'Grid',
          name: 'grid',
        },
        {
          label: 'Slider',
          name: 'slider',
        },
      ],
      profileFetchState: '',
      profileFetchMessage: '',
      selectedId: 0,
      datatable: {
        items: [],
        totalItems: 1,
        columns: [
          { key: 'id', label: '#', sortable: true },
          { key: 'widget_title', label: 'Widget', sortable: true },
          { key: 'instagram_page', label: 'Instagram Page', class: 'instagram_page' },
          { key: 'installation', label: 'Installation', class: 'installation' },
          { key: 'actions', label: 'Actions', class: 'actions' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          prospect_status: 'all',
          search: null,
          sort_by: 'id',
          sort_desc: true,
        },
      },
      profile: {
        username: '',
        name: '',
        profile_picture_url: '',
        followers_count: '',
        follows_count: '',
        media_count: '',
      },
      tokens: {
        accessToken: '',
        igID: '',
      },
      posts: [{
        'id': '',
        'like_count': '',
        'comments_count': '',
        'caption': '',
        'media_url': '',
      }],
      postsType: 'profile',
      widgetId: 0,
      newWidgetTitle: '',
      editor: {
        source: {
          username: '',
          type: 'username',
        },
        layout: {
          layoutStyle: 'grid',
          rows: 3,
          columns: 3,
          show_profile_details: true,
          profile_details: {
            profile_image: true,
            username: true,
            name: true,
            follow_button: true,
            post_count: true,
            followers: true,
            following: true,
          },
          padding: 5,
        },
        post_style: {
          post_style: 'image_card',
          post_details: {
            profile_image: true,
            username: true,
            likes_count: true,
            comment_count: true,
            caption: true,
          },
          post_action: 'instagram',
        },
        editing: 'source',
        styles: {
          theme: 'light',
          header_bg: '#ffffff',
          header_text: '#000000',
          follow_button_bg: '#0081ff',
          follow_button_text: '#ffffff',
          load_more_button_bg: '#0081ff',
          load_more_button_text: '#ffffff',
        },
      },
      default_editor: {
        source: {
          username: 'k.mbappe',
          type: 'username',
        },
        layout: {
          layoutStyle: 'grid',
          rows: 3,
          columns: 3,
          show_profile_details: true,
          profile_details: {
            profile_image: true,
            username: true,
            name: true,
            follow_button: true,
            post_count: true,
            followers: true,
            following: true,
          },
          padding: 5,
        },
        post_style: {
          post_style: 'image_card',
          post_details: {
            profile_image: true,
            username: true,
            likes_count: true,
            comment_count: true,
            caption: true,
          },
          post_action: 'instagram',
        },
        editing: 'layout',
        styles: {
          theme: 'light',
          header_bg: '#ffffff',
          header_text: '#000000',
          follow_button_bg: '#0081ff',
          follow_button_text: '#ffffff',
          load_more_button_bg: '#0081ff',
          load_more_button_text: '#ffffff',
        },
      },
      modalPost: {
        'id': '',
        'like_count': '',
        'comments_count': '',
        'caption': '',
        'media_url': '',
      },
      savingEditor: false,
      showCreateWidgetModal: false,
      showDeleteModal: false,
      selectedBusiness: null,
      loadingBusinesses: false,
      loadingWidgets: false,
      loadingImpersonate: false,
      loadingRemove: null,
      showWelcomeModal: false,
      createIsBusy: false,
      widgetDefaultText: '',
      showCreateLeadModal: false,
      showShippingModal: false,
      showEditWidgetModal: false,
      showPostModal: false,
      showAddWidgetModal: false,
      gettingWidget: false,
      gettingPage: false,
      selectedCustomer: null,
      customerOptions: [],
      shippingModalOwner: {
        first_name: '',
        last_name: '',
        id: null,
        tracking_number: '',
        working: false,
        tracking_numbers: [],
      },
      additionalPostsToShow: 0,
      widgets: [],
      selectedWidget: null,
    }
  },

  computed: {
    instaStreamGeneratorScriptSrc() {
      const isStaging = typeof window !== 'undefined' && window.location.origin.includes('staging.')
      return isStaging
        ? 'https://rengine.sfo3.cdn.digitaloceanspaces.com/staging/generator-scripts/v3/instastream-widget-generator.js'
        : 'https://rengine.sfo3.cdn.digitaloceanspaces.com/generator-scripts/v3/instastream-widget-generator.js'
    },
    embedHTML() {
      return `<!-- Start Instagram Widget -->
        &lt;script src="${this.instaStreamGeneratorScriptSrc}">&lt;/script>
            <instagram-widget widget-id='${this.selectedWidget}'></instagram-widget>
        <!-- End Instagram Widget -->
        `
    },
    widgetList() {
      let widgets = this.$store.getters['igwidget/all']
      console.log(widgets)
      return this.$store.getters['igwidget/all']
    },

    totalWidgets() {
      const widgets = this.$store.getters['igwidget/all']
      return widgets ? widgets.length : 0
    },

    user() {
      return this.$store.getters['auth/user']
    },

    routeName() {
      return this.$route.name
    },
    visiblePostCount() {
      return (this.editor.layout.columns * this.editor.layout.rows) + this.additionalPostsToShow
    },
    visiblePosts() {
      const maxPosts = (this.editor.layout.columns * this.editor.layout.rows) + this.additionalPostsToShow
      const filteredPosts = this.posts.filter(post => post.media_url)
      return filteredPosts.slice(0, maxPosts)
    },
    imageCardPosts() {
      return this.visiblePosts.filter(post =>
        this.editor.post_style.post_style === 'image_card' && post.media_url
      );
    },
    expandedPosts() {
      return this.visiblePosts.filter(post =>
        this.editor.post_style.post_style === 'expanded' && post.media_url
      );
    },
    showLoadMore() {
      console.log(this.visiblePostCount)
      console.log(this.posts.length)
      return this.visiblePostCount < this.posts.length
    },
  },

  watch: {
    reload: (val) => {
      console.log(val)
    },
    'editor.styles.theme': function (newTheme) {
      if (newTheme === 'dark') {
        this.editor.styles.header_bg = '#1b1b1b'
        this.editor.styles.header_text = '#ffffff'
        this.editor.styles.follow_button_bg = '#333333'
        this.editor.styles.follow_button_text = '#ffffff'
        this.editor.styles.load_more_button_bg = '#333333'
        this.editor.styles.load_more_button_text = '#ffffff'
      } else if (newTheme === 'light') {
        this.editor.styles.header_bg = '#ffffff'
        this.editor.styles.header_text = '#000000'
        this.editor.styles.follow_button_bg = '#0081ff'
        this.editor.styles.follow_button_text = '#ffffff'
        this.editor.styles.load_more_button_bg = '#0081ff'
        this.editor.styles.load_more_button_text = '#ffffff'
      }
    },
  },

  created() {
    this.getWidgets()
  },

  mounted() {
    this.getWidgets()
  },

  methods: {
    formatNumber(value) {
      if (typeof value !== 'number' || isNaN(value)) {
        return value
      }

      const thresholds = [
        { value: 1e9, suffix: 'B' },
        { value: 1e6, suffix: 'M' },
        { value: 1e3, suffix: 'K' },
      ]

      for (const { value: threshold, suffix } of thresholds) {
        if (value >= threshold) {
          return (value / threshold).toFixed(1) + suffix
        }
      }

      return value.toString()
    },

    addWidget(widgetId) {
      this.selectedWidget = widgetId
      this.showAddWidgetModal = true
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.embedHTML.replaceAll('&lt;', '<'))
      Vue.$toast.open({
        message: 'Code Copied',
        type: 'success',
        duration: 5000,
      })
    },

    loadMore() {
      this.additionalPostsToShow += this.editor.layout.columns

      console.log(this.additionalPostsToShow)
    },

    getWidget(fetchProfile = false) {
      const raw = (this.editor.source.username || '').trim().replace(/^@/, '')
      if (!raw) {
        this.gettingWidget = false
        this.profileFetchState = ''
        this.profile = null
        this.posts = []
        if (!fetchProfile) {
          this.showEditWidgetModal = true
        }
        return
      }
      const username = raw
      this.gettingWidget = true
      this.profileFetchState = 'fetching'

      const handleError = (message) => {
        Vue.$toast.open({
          message,
          type: 'error',
          duration: 5000,
        })
        this.gettingWidget = false
        this.profileFetchState = 'error'
        this.profileFetchMessage = message
        this.showEditWidgetModal = true
      }

      const getApiErrorMessage = (err) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error
        return (typeof msg === 'string' && msg) ? msg : null
      }

      const handleSuccess = (res, postsType) => {
        if (res.businessDetails || res) {
          this.profile = res.businessDetails ?? null
          this.posts = res.businessDetails?.media.data || res
          this.postsType = postsType
          this.gettingWidget = false
          this.profileFetchState = 'success'
          if (!fetchProfile) {
            this.showWidgetModal()
          }
        } else {
          handleError('Invalid Username or You\'re trying to access a private account')
        }
      }

      const fetchIgWidget = () => {
        this.$store.dispatch('igwidget/getIgWidget', { username })
          .then((res) => handleSuccess(res, 'profile'))
          .catch((err) => handleError(getApiErrorMessage(err) || 'Invalid Username or You\'re trying to access a private account'))
      }

      const searchKeyword = () => {
        this.$store.dispatch('igwidget/searchKeyword', { username })
          .then((res) => handleSuccess(res, 'hashtag'))
          .catch((err) => handleError(getApiErrorMessage(err) || 'Invalid Hashtag or You\'re trying to access a private account'))
      }
      if (this.editor.source.type === 'username') {
        fetchIgWidget()
      } else {
        searchKeyword()
      }
    },


    showWidgetModal() {
      this.showEditWidgetModal = true
    },

    post_action(post) {
      this.modalPost = post
      if (this.editor.post_style.post_action === 'instagram') {
        window.open(post.permalink, '_blank')
      } else {
        this.showPostModal = true
      }
    },

    getWidgets() {
      this.loadingWidgets = true

      this.$store
        .dispatch('igwidget/getAllWidgets', {
          queries: this.datatable.queries,
          page: 'index',
        })
        .then((res) => {
          this.loadingWidgets = false
          console.log(res)
          this.widgets = res
        })
        .catch((error) => {
          this.loadingWidgets = false
          console.error('Error fetching widgets:', error)
        })
    },

    normalizeInstagramUsername(value) {
      const raw = ('' + (value || '')).trim().replace(/^@/, '')
      const cleaned = raw.replace(/[^a-zA-Z0-9._]/g, '').toLowerCase()
      return cleaned || ''
    },
    buildCreateEditor() {
      const editor = JSON.parse(JSON.stringify(this.default_editor))
      const seeded = this.normalizeInstagramUsername(this.newWidgetTitle)
      if (seeded) {
        editor.source.username = seeded
      }
      return editor
    },

    async createWidget() {

      this.editor.editing = 'source'
      this.editor.source.username = ''
      this.profileFetchState = ''
      this.profile = {}
      this.posts = [{
        'id': '',
        'like_count': '',
        'comments_count': '',
        'caption': '',
        'media_url': '',
      }];
      this.gettingWidget = true
      console.log(this.newWidgetTitle)
      try {
        const response = await this.$store.dispatch('igwidget/createWidget', {
          editor: this.buildCreateEditor(),
          widgetTitle: this.newWidgetTitle,
        })
          .then((resp) => {
            this.widgetId = resp.data.id
            this.profileFetchState = ''
            this.profile = null
            this.posts = []
            this.showEditWidgetModal = true
          })
        this.gettingWidget = false
        console.log('Widget created successfully:', response)

        this.showCreateWidgetModal = false
      } catch (error) {
        this.loadingBusinesses = false
        console.error('Error creating widget:', error)
      }
    },

    widgetNameFromSettings(widget) {
      if (!widget || !widget.widget_settings) return null
      try {
        const s = typeof widget.widget_settings === 'string' ? JSON.parse(widget.widget_settings) : widget.widget_settings
        const username = s.source && s.source.username ? ('' + s.source.username).trim() : null
        return username || null
      } catch {
        return null
      }
    },

    editWidget(business) {
      this.widgetId = business.id
      this.editor = JSON.parse(business.widget_settings)
      const username = (this.editor.source && this.editor.source.username) ? ('' + this.editor.source.username).trim().replace(/^@/, '') : ''
      if (username) {
        this.getWidget()
      } else {
        this.profileFetchState = ''
        this.profile = null
        this.posts = []
        this.showEditWidgetModal = true
      }
    },

    redirectToProfile() {
      if (this.profile && this.profile.username) {
        window.open(`https://www.instagram.com/${this.profile.username}`, '_blank')
      }
    },

    viewPage(widgetSettings) {
      this.gettingPage = true
      const settings = JSON.parse(widgetSettings.widget_settings)
      const username = settings.source.username.replace('@', '');
      window.open(`https://www.instagram.com/${username}`, '_blank')
      this.gettingPage = false
    },

    async saveEditor(exit) {
      this.savingEditor = true

      try {
        const response = await this.$store.dispatch('igwidget/saveEditor', {
          editor: this.editor,
          widgetId: this.widgetId,
        })

        this.savingEditor = false
        console.log('Widget Saved Successfully:', response)
        this.getWidgets()

        if (exit) {
          this.showEditWidgetModal = false
        }
      } catch (error) {
        this.savingEditor = false
        console.error('Error Saving Widget:', error)
      }
    },

    remove(business) {
      this.showDeleteModal = true
      this.selectedBusiness = business
      this.widgetId = business.id
    },

    handleRemove() {
      if (!this.widgetId) return
      this.showDeleteModal = false
      this.loadingRemove = this.widgetId

      this.$store
        .dispatch('igwidget/deleteWidget', this.widgetId)
        .then(() => {
          this.loadingRemove = null
          this.widgetId = null
          this.selectedBusiness = null
          this.getWidgets()
          Vue.$toast.open({ message: 'Widget deleted successfully.', type: 'success', duration: 5000 })
        })
        .catch((err) => {
          this.loadingRemove = null
          const message = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Could not delete widget. Please try again.'
          Vue.$toast.open({ message, type: 'error', duration: 5000 })
          console.error('Error removing widget', err)
        })
    },

    sortChanged(args) {
      this.datatable.queries.sort_by = args.sortBy
      this.datatable.queries.sort_desc = args.sortDesc
      this.datatable.queries.page = 1
      this.getWidgets()
    },

    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getWidgets()
    },

    impersonate(userId) {
      this.loadingImpersonate = true

      this.$store
        .dispatch('auth/impersonate', userId)
        .then((res) => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/' + user.role.toLocaleLowerCase())
              this.loadingImpersonate = false
            })
            .catch(() => {
              this.loadingImpersonate = false
            })
        })
        .catch(() => {
          this.loadingImpersonate = false
        })
    },

  },
}
</script>

<style lang="scss">
@import "../../../design/custom/components/insta-widget-editor-preview";

.instagram-stream-idle-message {
  padding: 2rem;
  text-align: center;
}

/* Edit Instagram Widget - new design colors (variables on modal root so header inherits them) */
.edit_widget_modal.widget-editor.widget-modal {
  --insta-editor-sidebar: 222 47% 15%;
  --insta-editor-header: 222 47% 12%;
  --insta-editor-nav-active: 222 47% 22%;
  --insta-editor-card: 222 27% 22%;
  --insta-editor-text: 210 40% 98%;
  --insta-editor-muted: 215 20% 58%;
  --insta-editor-primary: 142 71% 45%;

  .modal-dialog {
    margin: 0;

    .modal-content {
      width: 100vw;
      height: 100vh;
      border-radius: 0;
    }
  }

  .modal-body {
    padding: 20px;
  }

  padding: 0 !important;

  .modal-dialog {
    width: 100%;
    height: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
  }

  .modal-content {
    height: auto;
    min-height: 100%;
    border-radius: 0;
    border: 0;
    display: flex !important;
    flex-direction: column !important;

    .modal-header {
      background: hsl(var(--insta-editor-header)) !important;
      color: hsl(var(--insta-editor-text)) !important;
      padding: 12px 20px !important;
      border-radius: 0 !important;
      border: 0 !important;
      flex-shrink: 0 !important;
    }

    .modal-body,
    .modal-body.edit-widget-modal-body-wrapper {
      padding: 0 !important;
      flex: 1 !important;
      min-height: 0 !important;
      display: flex !important;
      flex-direction: column !important;
    }
  }

  /* Force left sidebar layout: sidebar left, content right (override any tab layout) */
  .edit-widget-modal-body {
    display: flex !important;
    flex-direction: row !important;
    flex: 1 !important;
    min-height: 0 !important;
    width: 100% !important;
  }

  .edit-widget-modal-body .container-fluid {
    display: flex !important;
    flex: 1 !important;
    min-width: 0 !important;
    padding: 0 !important;
  }

  .edit-widget-modal-body .row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    flex: 1 !important;
    width: 100% !important;
    margin: 0 !important;
  }

  /* Override any imported .widget-editor .left-nav-side that might force row layout */
  .edit-widget-modal-body-wrapper .left-nav-side,
  .insta-widget-editor-sidebar .left-nav-side {
    width: 56px !important;
    min-width: 56px !important;
    font-size: 11px !important;
    margin-right: 0 !important;
    text-align: center !important;
    padding-top: 12px !important;
    display: flex !important;
    flex-direction: column !important;
    flex-wrap: nowrap !important;

    button {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      padding: 12px 8px !important;
      margin-bottom: 4px !important;
      border: 0 !important;
      border-radius: 0 !important;
      appearance: none !important;
      background: transparent !important;
      color: hsl(var(--insta-editor-muted)) !important;
      cursor: pointer !important;
      transition: color 0.15s, background 0.15s !important;

      i {
        font-size: 22px !important;
        line-height: 1.2 !important;
        margin-bottom: 4px !important;
      }

      &:hover {
        color: hsl(var(--insta-editor-text)) !important;
      }

      &.active {
        background: hsl(var(--insta-editor-nav-active)) !important;
        color: hsl(var(--insta-editor-text)) !important;
      }
    }
  }

  .sidebar.insta-widget-editor-sidebar {
    background: hsl(var(--insta-editor-sidebar)) !important;
    color: hsl(var(--insta-editor-text)) !important;
    padding: 20px 16px 20px 0 !important;
    min-height: calc(100vh - 52px) !important;
    max-width: 360px !important;
    width: 360px !important;
    flex: 0 0 360px !important;
    display: flex !important;
  }

  .editor-content,
  .editor-content.instagram-widget {
    flex: 1 !important;
    min-width: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  .instagram-widget {
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  /* Toggle switches - new design primary green */
  --track-width: 45px !important;
  --track-height: 25px !important;
  --track-active-color: hsl(var(--insta-editor-primary)) !important;

  .insta-widget-editor-sidebar .nav-sidebar {
    width: calc(100% - 56px) !important;
    padding-left: 16px !important;
    overflow-x: hidden !important;

    > li {
      list-style: none !important;
      width: 100% !important;

      > h4 {
        margin-top: 0 !important;
        margin-bottom: 16px !important;
        font-size: 1.25rem !important;
        font-weight: 600 !important;
        color: hsl(var(--insta-editor-text)) !important;
      }

      > ul {
        > li {
          background: hsl(var(--insta-editor-card)) !important;
          border-radius: 8px !important;
          padding: 10px 12px !important;
          margin-bottom: 10px !important;
          width: 100% !important;
          clear: both !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          color: hsl(var(--insta-editor-text)) !important;

          > span {
            flex: 1 !important;
            flex-wrap: nowrap !important;
            text-align: left !important;
          }

          .vue-js-switch {
            margin-bottom: 0 !important;
            flex-shrink: 0 !important;
            margin-left: auto !important;
          }
        }
      }
    }

    .layouts_wrapper {
      display: flex !important;
      gap: 10px !important;
      flex-wrap: wrap !important;

      .layout-item {
        flex: 1 !important;
        min-width: 0 !important;
        max-width: 50% !important;
      }

      .layout-item .btn,
      .layout-item label.btn,
      .layout-option-btn {
        background: hsl(var(--insta-editor-card)) !important;
        border: 2px solid hsl(var(--insta-editor-card)) !important;
        color: hsl(var(--insta-editor-text)) !important;
        border-radius: 8px !important;
        padding: 8px !important;
        width: 100% !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        min-height: 70px !important;

        img {
          max-width: 48px !important;
          max-height: 48px !important;
          margin-bottom: 4px !important;
        }

        span {
          font-size: 12px !important;
        }

        &:hover {
          border-color: hsl(var(--insta-editor-muted)) !important;
        }
      }

      .btn-check:checked + .btn,
      .btn-check:checked + label.btn,
      .btn-check:checked + .layout-option-btn {
        border-color: hsl(var(--insta-editor-primary)) !important;
        background: hsl(var(--insta-editor-nav-active)) !important;
      }
    }

    .source > ul > li {
      &.d-flex {
        display: flex !important;
        align-items: center !important;
        gap: 0 !important;

        .source_input {
          flex: 1 !important;
          min-width: 0 !important;
          margin-right: 0 !important;
        }

        .source_button {
          flex-shrink: 0 !important;
          margin-left: 4px !important;
        }
      }

      &.widget_theme .layout-item {
        width: 100% !important;
        align-items: center !important;
      }
    }

    .post_style_wrapper .layout-item,
    .post_action_wrapper .layout-item,
    .widget_theme .layout-item {
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      > span:first-child {
        flex: 1 !important;
      }
    }

    .header_styles {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      > span {
        flex: 1 !important;
      }
    }

    .profile_details {
      list-style: none !important;
      padding: 0 !important;
      margin: 0 0 10px 0 !important;
    }

    .profile_details li {
      background: hsl(var(--insta-editor-card)) !important;
      border-radius: 8px !important;
      padding: 6px 12px !important;
      margin-bottom: 4px !important;
      color: hsl(var(--insta-editor-text)) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      > span {
        flex: 1 !important;
        text-align: left !important;
      }

      .vue-js-switch {
        flex-shrink: 0 !important;
        margin-left: auto !important;
      }
    }

    .v-select {
      min-width: 120px !important;
      width: 120px !important;
      flex-shrink: 0 !important;

      .vs__open-indicator {
        display: none !important;
      }

      .vs__selected {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }
    }

    .post_style_wrapper .v-select,
    .post_action_wrapper .v-select {
      min-width: 150px !important;
      width: 150px !important;
    }

    .style-selector {
      label {
        min-height: 120px !important;

        &:hover {
          color: hsl(var(--insta-editor-text)) !important;
        }

        width: 50% !important;
        border: 4px solid hsl(var(--insta-editor-card)) !important;

        &.checked {
          border-color: hsl(var(--insta-editor-primary)) !important;
        }

        img {
          max-width: 100% !important;
          height: auto !important;
        }
      }
    }

    .radius-selector {
      label.btn {
        padding: 3px !important;
        margin-left: 7px !important;
        margin-bottom: 0 !important;
        background: hsl(var(--insta-editor-card)) !important;

        &.checked,
        &:hover {
          background: hsl(var(--insta-editor-primary)) !important;
        }

        img {
          max-height: 18px !important;
        }
      }
    }

    .v-select ul[role="listbox"] {
      z-index: 10000 !important;
      color: #000 !important;
      text-align: left !important;

      li {
        text-align: left !important;
        white-space: nowrap !important;
      }
    }
  }
}

.edit_widget_modal .widget_btn {
  background: #f2f4f3;
  border: 1px solid #474747;
  color: #213556;
}

/* Align Instagram profile header (avatar, name, stats, follow button) in a single horizontal row */
.instagram-widget .profile_details_container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: nowrap;
}

.instagram-widget .profile_details_left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.instagram-widget .profile_insights_container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
}

.instagram-widget .profile_insights_container .insight_count_tag {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;
}
</style>

<!-- Unscoped: vue-select dropdown is appended to body, needs global z-index to appear above modal -->
<style lang="scss">
.vs__dropdown-menu {
  z-index: 10000 !important;
}
</style>

<style lang="scss" scoped>
// Additional styles specific to new design
.duration-200 {
  transition-duration: 200ms;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

// Ensure all buttons in the table have proper styling
button {
  &.bg-destructive {
    border: none !important;
  }

  &:not(.bg-destructive):not(.bg-foreground) {
    border: 1px solid #d1d5db;
  }
}
</style>
